import { fetchAuthSession } from 'aws-amplify/auth';

const IS_MOCK = process.env.REACT_APP_MOCK_AUTH === 'true';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost/api';

/**
 * Default timeout for API calls in milliseconds.
 * Set to 30 seconds — slightly above API Gateway's 29-second integration timeout
 * so the backend has a chance to return a proper error before the frontend aborts.
 */
const API_TIMEOUT_MS = 30_000;

// Validate API base URL — skipped in mock mode
if (!IS_MOCK) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_URL environment variable is not configured');
  }

  // Validate URL format
  const validateApiUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  if (!validateApiUrl(API_BASE_URL)) {
    throw new Error('Invalid API base URL format');
  }
}

// Custom error for session expiry
export class SessionExpiredError extends Error {
  constructor(message: string = 'Your session has expired. Please log in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// Custom error for permission denied (403 that's not session related)
export class PermissionDeniedError extends Error {
  constructor(message: string = 'You do not have permission to perform this action.') {
    super(message);
    this.name = 'PermissionDeniedError';
  }
}

// Event emitter for session expiry
type SessionExpiredCallback = () => void;
let sessionExpiredCallback: SessionExpiredCallback | null = null;

export function onSessionExpired(callback: SessionExpiredCallback) {
  sessionExpiredCallback = callback;
}

// Helper to get valid auth token with proper error handling
async function getAuthToken(useAccessToken: boolean = false): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = useAccessToken 
      ? session.tokens?.accessToken?.toString()
      : session.tokens?.idToken?.toString();

    if (!token) {
      throw new SessionExpiredError();
    }

    return token;
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      throw error;
    }
    // Any auth error means session is invalid
    console.error('Auth session error:', error);
    throw new SessionExpiredError();
  }
}

// Handle API response errors
async function handleApiError(response: Response, error?: Error): Promise<never> {
  // If the response is HTML (e.g. CloudFront returning index.html for a 4xx
  // from API Gateway), the real error is masked. Surface a meaningful message
  // rather than letting JSON.parse blow up with "Unexpected token '<'".
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    // CloudFront CustomErrorResponses converts API 4xx → index.html (200 + HTML).
    // The actual HTTP status reaching the browser may already be 200, so use
    // the status we have plus a clear message.
    const htmlError = new Error(
      `API returned HTML instead of JSON (status ${response.status}). ` +
      `This usually means CloudFront served the SPA fallback page instead of forwarding the API error. ` +
      `Check CloudFront CustomErrorResponses and the Cognito authorizer.`
    );
    (htmlError as any).status = response.status;
    throw htmlError;
  }

  // 401 is always session expired
  if (response.status === 401) {
    const sessionError = new SessionExpiredError();
    if (sessionExpiredCallback) {
      sessionExpiredCallback();
    }
    throw sessionError;
  }
  
  // 403 could be permission denied OR session expired (depending on the error message)
  if (response.status === 403) {
    try {
      const errorBody = await response.json();
      const errorMessage = errorBody.error || errorBody.message || '';
      
      // Check if it's an auth/session related 403
      const isSessionError = errorMessage.toLowerCase().includes('token') ||
                            errorMessage.toLowerCase().includes('session') ||
                            errorMessage.toLowerCase().includes('unauthorized') ||
                            errorMessage.toLowerCase().includes('access denied');
      
      if (isSessionError) {
        const sessionError = new SessionExpiredError();
        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        }
        throw sessionError;
      }
      
      // It's a permission denied error - throw with the actual message
      throw new PermissionDeniedError(errorMessage || 'You do not have permission to perform this action.');
    } catch (parseError) {
      if (parseError instanceof SessionExpiredError || parseError instanceof PermissionDeniedError) {
        throw parseError;
      }
      // If we can't parse the response, treat as permission denied
      throw new PermissionDeniedError();
    }
  }
  
  // For other errors, try to get the error message from response.
  // Attach the HTTP status so callers can branch on it (e.g. 409 duplicate upload)
  // instead of fragile substring-matching on the prose message.
  try {
    const errorBody = await response.json();
    const err = new Error(errorBody.error || errorBody.message || response.statusText);
    (err as any).status = response.status;
    throw err;
  } catch (parseError) {
    if (parseError instanceof Error && parseError.message !== response.statusText) {
      throw parseError;
    }
    const err = error || new Error('Request failed: ' + response.statusText);
    (err as any).status = response.status;
    throw err;
  }
}

export async function apiCall(endpoint: string, options: RequestInit = {}) {
  // In mock mode return an empty successful response — no network needed
  if (IS_MOCK) {
    console.warn(`[MOCK] apiCall skipped: ${endpoint}`);
    return {};
  }
  try {
    const token = await getAuthToken(false); // Use ID token
    
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    // Create AbortController for timeout — 30s slightly exceeds API Gateway's 29s limit
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(API_BASE_URL + sanitizedEndpoint, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      await handleApiError(response);
    }

    // Guard against CloudFront returning index.html (200 + text/html) instead of JSON.
    // This happens when CloudFront's CustomErrorResponses intercepts a 403/404 from
    // any origin (including API Gateway) and substitutes the SPA fallback page,
    // converting the status to 200. response.ok passes, but the body is HTML.
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      throw new Error('Permission denied: you do not have access to this resource.');
    }

    return await response.json();
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      if (sessionExpiredCallback) {
        sessionExpiredCallback();
      }
      throw error;
    }
    if (error instanceof PermissionDeniedError) {
      // Don't trigger session expiry for permission errors
      throw error;
    }
    // Handle abort (timeout) errors with a user-friendly message
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    console.error('API call failed:', error);
    throw error;
  }
}

// MFA API call - uses Access Token instead of ID Token
// Cognito MFA APIs (associateSoftwareToken, verifySoftwareToken, etc.) require Access Token
export async function mfaApiCall(endpoint: string, options: RequestInit = {}) {
  if (IS_MOCK) {
    console.warn(`[MOCK] mfaApiCall skipped: ${endpoint}`);
    return {};
  }
  try {
    const token = await getAuthToken(true); // Use Access token for MFA
    
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    // Create AbortController for timeout — 30s slightly exceeds API Gateway's 29s limit
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(API_BASE_URL + sanitizedEndpoint, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      await handleApiError(response);
    }

    // Same CloudFront HTML-substitution guard as apiCall
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      throw new Error('Permission denied: you do not have access to this resource.');
    }

    return await response.json();
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      if (sessionExpiredCallback) {
        sessionExpiredCallback();
      }
      throw error;
    }
    if (error instanceof PermissionDeniedError) {
      throw error;
    }
    // Handle abort (timeout) errors with a user-friendly message
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    console.error('MFA API call failed:', error);
    throw error;
  }
}

export interface MFAStatus {
  mfaRequired: boolean;
  mfaSetupComplete: boolean;
  mfaSetupDate: string | null;
  mfaLastUsed: string | null;
  mfaRequiredBy: string | null;
  mfaRequiredDate: string | null;
}

// Fetches the signed-in user's MFA status.
// Returns null when the status can't be determined, so callers decide how to react.
export async function fetchMFAStatus(): Promise<MFAStatus | null> {
  try {
    // Must use mfaApiCall (access token), not apiCall (ID token): the auth Lambda's
    // verifier is configured with tokenUse:'access', so an ID token is rejected with
    // 401. Because isMFASetupPending() fails open, sending the wrong token would
    // silently disable the admin-required-MFA gate rather than surfacing an error.
    // Ref: backend/functions/auth/index.js — tokenUse: 'access'
    return await mfaApiCall('/auth/mfa/status');
  } catch (error) {
    console.error('Could not determine MFA status:', error);
    return null;
  }
}

// True when an administrator requires MFA but the user hasn't enrolled yet.
//
// This is the access gate used after login, after a forced password change, and on
// session restore. It deliberately fails OPEN: if the status endpoint is
// unreachable we let the user through rather than locking out the whole portal on a
// transient network error. The requirement is re-checked on every subsequent entry.
export async function isMFASetupPending(): Promise<boolean> {
  const status = await fetchMFAStatus();
  if (!status) return false;
  return Boolean(status.mfaRequired) && !status.mfaSetupComplete;
}

// Public API call for auth endpoints that don't require authentication
export async function publicApiCall(endpoint: string, options: RequestInit = {}) {
  if (IS_MOCK) {
    console.warn(`[MOCK] publicApiCall skipped: ${endpoint}`);
    return {};
  }
  try {
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    // Create AbortController for timeout — 30s slightly exceeds API Gateway's 29s limit
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(API_BASE_URL + sanitizedEndpoint, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      try {
        const errorBody = await response.json();
        throw new Error(errorBody.error || errorBody.message || response.statusText);
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message !== response.statusText) {
          throw parseError;
        }
        throw new Error('Request failed: ' + response.statusText);
      }
    }

    return await response.json();
  } catch (error) {
    // Handle abort (timeout) errors with a user-friendly message
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    console.error('Public API call failed:', error);
    throw error;
  }
}
